// Offers: the pairs behind the upsell and downsell messages and the in-salon "Suggested" services. HQ sets the
// organization's rules; a branch can add its own, which win over HQ's for the same service. Each rule shows what it
// suggests at the branch in view, with that branch's price. "Try it" shows what a customer would be offered now.
// The messages themselves are automations (Automations → "Upsell…" and "Visited, service not taken…").
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useEngagementScope } from '../context/EngagementScope';
import BranchSelect from '../components/BranchSelect';
import { Confirm, EmptyState, Loader, Modal, PageHead, Spinner } from '../components/ui';

const KINDS = [
  { id: 'upsell', label: 'Upsell', help: 'Days after a service is taken: a service that pairs with it (Haircut → Hair spa).' },
  { id: 'downsell', label: 'Downsell', help: 'After a visit where the service was not taken: a cheaper alternative. Without a rule, the next cheaper service of the same category.' },
  { id: 'addon', label: 'In-salon add-on', help: 'Shown as “Suggested” in Add service during a visit.' },
];
const EMPTY = {
  when_type: 'service',
  when_value: '',
  suggest_type: 'service',
  suggest_value: '',
  offer_text: '',
  discount_pct: 0,
  valid_days: 14,
  delay_days: 0,
  priority: 100,
  enabled: true,
  franchise_id: '',
};

function Picker({ type, value, onChange, services, categories }) {
  const list = type === 'category' ? categories : [...new Set(services.map((s) => s.name))].sort();
  return (
    <select className="de-select" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{type === 'category' ? 'Choose a category…' : 'Choose a service…'}</option>
      {list.map((v) => (
        <option key={v} value={v}>
          {v}
        </option>
      ))}
    </select>
  );
}

function RuleModal({ kind, initial, services, categories, branches, canEditAll, ownBranch, onClose, onSaved }) {
  const [form, setForm] = useState(
    initial
      ? { ...EMPTY, ...initial, franchise_id: initial.franchise_id ?? '' }
      : { ...EMPTY, franchise_id: canEditAll ? '' : ownBranch || '' },
  );
  const [problems, setProblems] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const save = async () => {
    setBusy(true);
    setProblems([]);
    const body = {
      ...form,
      kind,
      franchise_id: form.franchise_id === '' ? null : Number(form.franchise_id),
      discount_pct: Number(form.discount_pct) || 0,
      valid_days: Number(form.valid_days) || 14,
      delay_days: Number(form.delay_days) || 0,
      priority: Number(form.priority) || 100,
    };
    try {
      if (initial?.id) await api.updateOfferRule(initial.id, body);
      else await api.createOfferRule(body);
      toast.success('Saved');
      onSaved();
    } catch (e) {
      if (e.data?.problems) setProblems(e.data.problems);
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={`${initial?.id ? 'Edit' : 'New'} ${KINDS.find((k) => k.id === kind).label.toLowerCase()} rule`} onClose={onClose} size="wide">
      <div className="de-form-grid de-form-grid--2">
        <label className="de-field">
          <span className="de-label">After</span>
          <select className="de-select" value={form.when_type} onChange={(e) => setForm((f) => ({ ...f, when_type: e.target.value, when_value: '' }))}>
            <option value="service">This service</option>
            <option value="category">Any service in this category</option>
          </select>
        </label>
        <label className="de-field">
          <span className="de-label">&nbsp;</span>
          <Picker type={form.when_type} value={form.when_value} onChange={(v) => setForm((f) => ({ ...f, when_value: v }))} services={services} categories={categories} />
        </label>
        <label className="de-field">
          <span className="de-label">Suggest</span>
          <select className="de-select" value={form.suggest_type} onChange={(e) => setForm((f) => ({ ...f, suggest_type: e.target.value, suggest_value: '' }))}>
            <option value="service">This service</option>
            <option value="category">{kind === 'downsell' ? 'The next cheaper in this category' : 'The top service in this category'}</option>
          </select>
        </label>
        <label className="de-field">
          <span className="de-label">&nbsp;</span>
          <Picker type={form.suggest_type} value={form.suggest_value} onChange={(v) => setForm((f) => ({ ...f, suggest_value: v }))} services={services} categories={categories} />
        </label>
        <label className="de-field" style={{ gridColumn: '1 / -1' }}>
          <span className="de-label">Offer text (optional, shown in the message)</span>
          <input className="de-input de-input--bordered" maxLength={300} value={form.offer_text} onChange={set('offer_text')} placeholder="10% off if you book this week." />
        </label>
        <label className="de-field">
          <span className="de-label">Discount %</span>
          <input className="de-input de-input--bordered" type="number" min={0} max={90} value={form.discount_pct} onChange={set('discount_pct')} />
        </label>
        <label className="de-field">
          <span className="de-label">A booking within (days) counts as from the offer</span>
          <input className="de-input de-input--bordered" type="number" min={1} max={90} value={form.valid_days} onChange={set('valid_days')} />
        </label>
        {kind === 'upsell' ? (
          <label className="de-field">
            <span className="de-label">Send after (days; 0 = the automation’s wait)</span>
            <input className="de-input de-input--bordered" type="number" min={0} max={180} value={form.delay_days} onChange={set('delay_days')} />
          </label>
        ) : null}
        <div className="de-field">
          <span className="de-label">Applies to</span>
          <BranchSelect
            branches={branches}
            value={form.franchise_id}
            onChange={(next) => setForm((current) => ({ ...current, franchise_id: next }))}
            emptyLabel={canEditAll ? 'Every branch (HQ)' : ''}
            placeholder="Choose the branch…"
            disabled={!canEditAll}
          />
        </div>
        <label className="de-field">
          <span className="de-label">Priority (lower first)</span>
          <input className="de-input de-input--bordered" type="number" value={form.priority} onChange={set('priority')} />
        </label>
        <label className="de-row" style={{ gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={form.enabled} onChange={set('enabled')} />
          <span className="de-small">On</span>
        </label>
      </div>
      {problems.length ? (
        <div className="de-callout de-mt-16" style={{ borderColor: 'var(--de-danger)' }}>
          {problems.map((p) => (
            <div key={p} className="de-small">
              {p}
            </div>
          ))}
        </div>
      ) : null}
      <div className="de-form-actions" style={{ marginTop: 18 }}>
        <button type="button" className="de-btn de-btn--outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="de-btn de-btn--primary" disabled={busy || !form.when_value || !form.suggest_value} onClick={save}>
          {busy ? <Spinner light /> : null}
          Save
        </button>
      </div>
    </Modal>
  );
}

function TryIt({ kind, services, branch }) {
  const [service, setService] = useState('');
  const [result, setResult] = useState(null);
  useEffect(() => {
    setResult(null);
    if (!service || !branch) return;
    api
      .tryOffer({ kind, service_name: service, franchise_id: branch })
      .then(setResult)
      .catch((e) => toast.error(e.message));
  }, [service, kind, branch]);
  return (
    <div className="de-card" style={{ marginBottom: 14 }}>
      <div className="de-row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className="de-td-strong">Try it:</span>
        <span className="de-small">a customer who had</span>
        <div style={{ width: 300 }}>
          <Picker type="service" value={service} onChange={setService} services={services} categories={[]} />
        </div>
        <span className="de-small">is offered</span>
        {result ? (
          result.offer ? (
            <span className="de-pill de-pill--ok">
              {result.offer.service_name} · {result.offer.price}
              {result.offer.rule_id ? '' : ' (no rule: next cheaper)'}
            </span>
          ) : (
            <span className="de-pill de-pill--muted">nothing</span>
          )
        ) : (
          <span className="de-muted">—</span>
        )}
      </div>
    </div>
  );
}

export default function Offers() {
  const scope = useEngagementScope();
  const soleBranch = scope.selectedIds.length === 1 ? scope.selectedIds[0] : '';
  const [kind, setKind] = useState('upsell');
  const [branch, setBranch] = useState(soleBranch || (scope.branches[0] ? String(scope.branches[0].franchise_id) : ''));
  const [data, setData] = useState(null);
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  // The branch in view, once the signed-in user's branches have loaded (prices and services come from it).
  useEffect(() => {
    if (!branch && scope.branches.length) setBranch(soleBranch || String(scope.branches[0].franchise_id));
  }, [scope.branches, soleBranch, branch]);

  const load = () =>
    api
      .listOfferRules({ kind, franchise_id: branch || undefined })
      .then(setData)
      .catch((e) => toast.error(e.message));

  useEffect(() => {
    load();
  }, [kind, branch]);

  useEffect(() => {
    if (!branch) return;
    api
      .listServices(branch)
      .then((res) => setServices(res?.services || []))
      .catch(() => setServices([]));
  }, [branch]);

  const categories = useMemo(() => [...new Set(services.map((s) => s.category).filter(Boolean))].sort(), [services]);
  const names = Object.fromEntries(scope.branches.map((b) => [b.franchise_id, b.label || b.name]));
  const info = KINDS.find((k) => k.id === kind);

  const remove = async () => {
    setBusy(true);
    try {
      await api.deleteOfferRule(deleting.id);
      toast.success('Deleted');
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="de-fade">
      <PageHead
        title="Offers"
        sub="Which service to suggest after another: for the upsell and downsell messages and the in-salon “Suggested” services. A branch’s own rule wins over HQ’s."
        action={
          <button type="button" className="de-btn de-btn--primary" onClick={() => setEditing({})}>
            + New rule
          </button>
        }
      />
      <div className="de-tabs" style={{ borderRadius: 0 }}>
        {KINDS.map((k) => (
          <button key={k.id} type="button" className={`de-tab${kind === k.id ? ' active' : ''}`} onClick={() => setKind(k.id)}>
            {k.label}
          </button>
        ))}
      </div>
      <div className="de-tab-body">
        <div className="de-row" style={{ justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          <div className="de-small de-muted" style={{ maxWidth: 640 }}>
            {info.help}
          </div>
          {scope.branches.length > 1 ? (
            <div className="de-row" style={{ gap: 6, alignItems: 'center' }}>
              <span className="de-small">Prices at</span>
              <div style={{ width: 260 }}>
                <BranchSelect branches={scope.branches} value={branch} onChange={setBranch} placeholder="Choose the branch…" />
              </div>
            </div>
          ) : null}
        </div>
        {kind !== 'addon' ? <TryIt kind={kind} services={services} branch={branch} /> : null}
        {!data ? (
          <Loader />
        ) : data.rules.length ? (
          <div className="de-table-shell">
            <div className="de-table-wrap">
              <table className="de-table">
                <thead>
                  <tr>
                    <th>After</th>
                    <th>Suggest</th>
                    <th>At this branch</th>
                    <th>Applies to</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rules.map((r) => {
                    const editable = r.franchise_id == null ? data.can_edit_all : true;
                    return (
                      <tr key={r.id}>
                        <td className="de-td-strong">
                          {r.when_value}
                          <div className="de-small de-muted">{r.when_type === 'category' ? 'any service in this category' : 'service'}</div>
                        </td>
                        <td>
                          {r.suggest_value}
                          <div className="de-small de-muted">
                            {r.suggest_type === 'category' ? 'from this category' : 'service'}
                            {r.discount_pct ? ` · ${r.discount_pct}% off` : ''}
                            {r.kind === 'upsell' && r.delay_days ? ` · after ${r.delay_days} days` : ''}
                          </div>
                          {r.offer_text ? <div className="de-small">“{r.offer_text}”</div> : null}
                        </td>
                        <td className="de-small" style={{ maxWidth: 280 }}>
                          {r.preview ? (
                            r.preview.available ? (
                              r.preview.text
                            ) : (
                              <span className="de-warn-text">{r.preview.text}</span>
                            )
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="de-small">{r.franchise_id == null ? 'Every branch (HQ)' : names[r.franchise_id] || `Branch ${r.franchise_id}`}</td>
                        <td>
                          <span className={`de-pill de-pill--sm de-pill--${r.enabled ? 'ok' : 'muted'}`}>{r.enabled ? 'on' : 'off'}</span>
                        </td>
                        <td className="de-td-right" style={{ whiteSpace: 'nowrap' }}>
                          {editable ? (
                            <div className="de-row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                              <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setEditing(r)}>
                                Edit
                              </button>
                              <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setDeleting(r)}>
                                Delete
                              </button>
                            </div>
                          ) : (
                            <span className="de-small de-muted">Set by HQ</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <EmptyState icon="🎁" title="No rules yet" body={kind === 'downsell' ? 'Without rules, the next cheaper service of the same category is offered.' : 'Add the first pair.'} />
        )}
      </div>
      {editing ? (
        <RuleModal
          kind={kind}
          initial={editing.id ? editing : null}
          services={services}
          categories={categories}
          branches={scope.branches}
          canEditAll={Boolean(data?.can_edit_all)}
          ownBranch={soleBranch || (scope.scopeType === 'franchise' && scope.branches[0] ? String(scope.branches[0].franchise_id) : '')}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      ) : null}
      {deleting ? (
        <Confirm
          title="Delete this rule?"
          message={`${deleting.when_value} → ${deleting.suggest_value}`}
          confirmLabel="Delete"
          busy={busy}
          onCancel={() => setDeleting(null)}
          onConfirm={remove}
        />
      ) : null}
    </div>
  );
}
